import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import "swiper/css/navigation";
import Swiper from "swiper";
import { Autoplay, FreeMode } from "swiper/modules";

export class AutoTranslateSlider {
    private readonly container: HTMLElement;
    private readonly swiperContainer: HTMLElement;
    private slides: NodeListOf<HTMLElement>;
    private swiper: Swiper;
    private readonly swiperWrapper: HTMLElement;
    private translateOnScrollBreakpoint: number = 992;
    private scrollActive: boolean = false;

    constructor(container: HTMLElement) {
        this.container = container;
        this.swiperContainer = container.querySelector(".swiper");
        this.swiperWrapper = this.container.querySelector(".swiper-wrapper");
        this.slides = this.swiperWrapper.querySelectorAll(".swiper-slide");

        this.initSwiper();

        const addScrollListener = () => {
            if (this.swiper) {
                this.swiper.autoplay.stop();
                this.scrollActive = true;
                window.addEventListener("scroll", this.autoTranslate);
            }
        };

        // translate on scroll should only be active for laptops and desktops
        if (!this.scrollActive && window.innerWidth >= this.translateOnScrollBreakpoint) {
            addScrollListener();
        }

        window.addEventListener("resize", () => {
            this.swiper.autoplay.stop();

            if (!this.scrollActive && window.innerWidth >= this.translateOnScrollBreakpoint) {
                addScrollListener();
            }
            if (this.scrollActive && window.innerWidth < this.translateOnScrollBreakpoint) {
                this.scrollActive = false;
                this.swiper.autoplay.start();
                window.removeEventListener("scroll", this.autoTranslate);
            }
        });

        this.slides.forEach((slide) => {
            slide.addEventListener("focus", (event) => this.handleSlideFocus(event), true);
        });
    }

    private initSwiper = () => {
        this.swiper = new Swiper(this.swiperContainer, {
            modules: [Autoplay, FreeMode],
            direction: "horizontal",
            loop: false,
            breakpoints: {
                640: {
                    slidesPerView: 1,
                    centeredSlides: true,
                },
                768: {
                    slidesPerView: 2,
                },
                1024: {
                    slidesPerView: 3,
                },
            },
            freeMode: { enabled: true, sticky: false, momentum: false },
            spaceBetween: 32,
            autoplay: {
                delay: 3000,
                pauseOnMouseEnter: true,
            },
        });
    };

    private autoTranslate = () => {
        const rect = this.swiperWrapper.getBoundingClientRect();
        // if already scrolled left on page load we need the distance between the first slide and the last
        const firstRect = this.slides[0].getBoundingClientRect();
        const lastRect = this.slides[this.slides.length - 1].getBoundingClientRect();
        const maxTranslate = Math.abs(lastRect.right - firstRect.left - rect.width);
        const windowHeight = window.innerHeight;
        const bottom = rect.bottom + windowHeight * 0.1;
        const currentTranslate = this.swiper.getTranslate();
        const topBorder = bottom + rect.height;

        if (topBorder < windowHeight) {
            this.updateSwiperPosition(maxTranslate * -1);
        } else if (bottom > windowHeight && currentTranslate !== 0) {
            this.updateSwiperPosition(0);
        } else if (topBorder >= windowHeight && bottom <= windowHeight) {
            const delta = bottom - windowHeight;
            const percentage = delta / rect.height;
            this.updateSwiperPosition(maxTranslate * percentage);
        }
    };

    private updateSwiperPosition = (translateX: number) => {
        this.swiper.translateTo(translateX, 250, true, true);
    };

    private handleSlideFocus = (event: FocusEvent) => {
        const slide = event.target as HTMLElement;

        if (slide.matches(":focus-visible")) {
            const slideRect = slide.getBoundingClientRect();
            const swiperRect = this.swiperWrapper.getBoundingClientRect();

            // check if the slide is fully visible within the swiper wrapper
            if (slideRect.left <= swiperRect.left || slideRect.right >= swiperRect.right) {
                let translateX = 0;

                if (slideRect.left < swiperRect.left) {
                    translateX = slideRect.left - swiperRect.left;
                } else if (slideRect.right > swiperRect.right) {
                    translateX = slideRect.right - swiperRect.right;
                }

                this.swiper.setTranslate(-translateX);
            }
        }
    };
}
